/**
 * Keyboard-wedge scanner input. Captures scans only when no other field is being edited.
 */
import React, { useEffect, useRef, useState } from 'react';

export function isEditingOtherField(active) {
  if (!active || active === document.body) return false;
  const tag = (active.tagName || '').toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  if (tag === 'input') {
    const type = (active.getAttribute('type') || 'text').toLowerCase();
    return !['button', 'submit', 'checkbox', 'radio', 'hidden'].includes(type);
  }
  return !!active.isContentEditable;
}

export default function ScanInput({ onScan, disabled = false, label = 'Scan code' }) {
  const [value, setValue] = useState('');
  const [blocked, setBlocked] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const onFocusIn = () => {
      const active = document.activeElement;
      if (active === inputRef.current) {
        setBlocked(false);
        return;
      }
      setBlocked(isEditingOtherField(active));
    };
    document.addEventListener('focusin', onFocusIn);
    onFocusIn();
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  const submit = (raw) => {
    const code = (raw || '').trim();
    if (!code || disabled || blocked) return;
    onScan(code);
    setValue('');
  };

  return (
    <form
      className="scan-input"
      data-testid="scan-input"
      onSubmit={(e) => {
        e.preventDefault();
        submit(value);
      }}
    >
      <label>
        {label}
        <input
          ref={inputRef}
          data-testid="scan-input-field"
          value={value}
          disabled={disabled || blocked}
          autoComplete="off"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit(value);
            }
          }}
        />
      </label>
      {blocked && (
        <p role="status" data-testid="scan-input-blocked">
          Finish the field you are editing before scanning.
        </p>
      )}
    </form>
  );
}
